import { useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { Phone, Clock, MapPin, CheckCircle2, Loader2, Warehouse } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHeading } from "@/components/Reveal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICES = [
  "Front Dash Cam Installation",
  "Front + Rear Dual Channel",
  "Hardwire / Parking Mode",
  "Fleet / Commercial",
  "Supply + Install Bundle",
  "Other / Not Sure Yet",
];

const initialForm = { name: "", phone: "", email: "", vehicle: "", service: SERVICES[0], message: "" };

const inputCls =
  "w-full bg-obsidian border border-white/15 rounded-md px-4 py-3.5 text-sm text-gray-100 placeholder:text-gray-600 focus:outline-none focus:border-sky-500/70 focus:ring-1 focus:ring-sky-500/40 transition-[border-color,box-shadow] duration-300";

const Contact = () => {
  const [form, setForm] = useState(initialForm);
  const [sending, setSending] = useState(false);
  const [reference, setReference] = useState(null);

  const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/enquiries`, form);
      setReference(data.reference);
      setForm(initialForm);
    } catch (err) {
      const detail = err.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Could not send enquiry. Please call us instead.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-form-section" className="relative border-t border-white/10 bg-carbon/40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-5 sm:px-8 py-24 sm:py-32">
        <SectionHeading eyebrow="Get a quote" title="Book your install" testId="contact-heading" />

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-2">
            <div className="space-y-6">
              <a
                data-testid="contact-phone-card"
                href="tel:0415414296"
                className="group flex items-center gap-5 rounded-xl border border-sky-500/30 bg-obsidian p-6 sky-glow hover:border-sky-500/70 transition-colors duration-300"
              >
                <span className="p-3 rounded-md bg-sky-500 text-obsidian">
                  <Phone size={22} />
                </span>
                <span>
                  <span className="block font-mono text-[10px] uppercase tracking-[0.3em] text-gray-500">Call or text</span>
                  <span className="block font-display font-black text-3xl text-gray-50 group-hover:text-sky-400 transition-colors">0415 414 296</span>
                </span>
              </a>
              <div className="flex items-start gap-4 text-sm text-gray-400">
                <Clock size={18} className="text-sky-500 mt-0.5 shrink-0" />
                <p>Mon–Sat, 8am–6pm<br />Same-week installs usually available</p>
              </div>
              <div className="flex items-start gap-4 text-sm text-gray-400">
                <MapPin size={18} className="text-sky-500 mt-0.5 shrink-0" />
                <p>Mobile service across all Perth metro suburbs — we come to your home or workplace</p>
              </div>
              <div className="flex items-start gap-4 text-sm text-gray-400">
                <Warehouse size={18} className="text-sky-500 mt-0.5 shrink-0" />
                <p>Or book into our workshop:<br /><span className="text-gray-200 font-medium">U12/26 Ryelane St, Maddington WA</span></p>
              </div>
              <div className="rounded-lg overflow-hidden border border-white/10 hover:border-sky-500/30 transition-colors duration-300">
                <iframe
                  title="Autoric workshop — U12/26 Ryelane St, Maddington"
                  data-testid="workshop-map"
                  src="https://maps.google.com/maps?q=U12%2F26%20Ryelane%20St%2C%20Maddington%20WA%206109&t=&z=15&ie=UTF8&iwloc=&output=embed"
                  className="w-full h-52 grayscale-[30%] contrast-[1.05]"
                  loading="lazy"
                />
              </div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-gray-600 pt-4">
                Avg. response time: under 2 hours
              </p>
            </div>
          </Reveal>

          <Reveal delay={0.15} className="lg:col-span-3">
            <div className="rounded-xl border border-white/10 bg-obsidian p-6 sm:p-9">
              <AnimatePresence mode="wait">
                {reference ? (
                  <motion.div
                    key="success"
                    data-testid="enquiry-success"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="flex flex-col items-center text-center py-12"
                  >
                    <CheckCircle2 size={52} className="text-sky-500" />
                    <h3 className="mt-6 font-display font-bold uppercase text-3xl text-gray-50">Enquiry received</h3>
                    <p className="mt-3 text-sm text-gray-400 max-w-sm">
                      Thanks — we'll be in touch shortly to confirm your quote and install window.
                    </p>
                    <p className="mt-6 font-mono text-xs uppercase tracking-[0.3em] text-gray-500">
                      Reference: <span className="text-sky-400" data-testid="enquiry-reference">{reference}</span>
                    </p>
                    <button
                      data-testid="enquiry-another-btn"
                      onClick={() => setReference(null)}
                      className="mt-8 border border-white/20 text-gray-200 font-display font-bold uppercase tracking-widest text-sm px-6 py-3 rounded-md hover:border-sky-500/60 hover:text-sky-400 transition-colors"
                    >
                      Send another enquiry
                    </button>
                  </motion.div>
                ) : (
                  <motion.form
                    key="form"
                    data-testid="enquiry-form"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    onSubmit={submit}
                    className="grid grid-cols-1 sm:grid-cols-2 gap-4"
                  >
                    <div>
                      <label htmlFor="enquiry-name" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Name *</label>
                      <input id="enquiry-name" data-testid="enquiry-name-input" required value={form.name} onChange={set("name")} placeholder="Your name" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-phone" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Phone *</label>
                      <input id="enquiry-phone" data-testid="enquiry-phone-input" required type="tel" value={form.phone} onChange={set("phone")} placeholder="04XX XXX XXX" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-email" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Email *</label>
                      <input id="enquiry-email" data-testid="enquiry-email-input" required type="email" value={form.email} onChange={set("email")} placeholder="you@email.com" className={inputCls} />
                    </div>
                    <div>
                      <label htmlFor="enquiry-vehicle" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Vehicle *</label>
                      <input id="enquiry-vehicle" data-testid="enquiry-vehicle-input" required value={form.vehicle} onChange={set("vehicle")} placeholder="e.g. 2022 Mazda CX-5" className={inputCls} />
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enquiry-service" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Service required *</label>
                      <select id="enquiry-service" data-testid="enquiry-service-select" value={form.service} onChange={set("service")} className={inputCls}>
                        {SERVICES.map((s) => (
                          <option key={s} value={s}>{s}</option>
                        ))}
                      </select>
                    </div>
                    <div className="sm:col-span-2">
                      <label htmlFor="enquiry-message" className="block font-mono text-[10px] uppercase tracking-[0.25em] text-gray-500 mb-2">Message</label>
                      <textarea id="enquiry-message" data-testid="enquiry-message-input" rows={4} value={form.message} onChange={set("message")} placeholder="Suburb, preferred day, camera brand you already own..." className={`${inputCls} resize-none`} />
                    </div>
                    <button
                      data-testid="enquiry-submit-btn"
                      type="submit"
                      disabled={sending}
                      className="sm:col-span-2 flex items-center justify-center gap-3 bg-sky-500 text-obsidian font-display font-bold uppercase tracking-widest text-base py-4 rounded-md hover:bg-sky-400 hover:shadow-[0_0_30px_rgba(14,165,233,0.4)] disabled:opacity-60 transition-[background-color,box-shadow] duration-300"
                    >
                      {sending && <Loader2 size={18} className="animate-spin" />}
                      {sending ? "Sending..." : "Send Enquiry"}
                    </button>
                  </motion.form>
                )}
              </AnimatePresence>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default Contact;
